/**
 * What switching an envelope between "everyone signs one document" and "each
 * person signs their own copy" would throw away.
 *
 * The server carries what it can across a switch, but some of a draft has no
 * equivalent on the other side — a viewer has no place on a one-signer copy,
 * and fields placed for two different signers become one set every person
 * fills in. None of it comes back on switching again, so the builder states
 * each loss and asks before switching. An empty list means nothing is lost
 * and the switch needs no confirmation.
 *
 * Mirrors `setEnvelopeSendMode` in `@fias/db-operations` — change both together.
 */

import type { DocumentSignEnvelopeDetailResponse, DocumentSignSendMode } from '@/lib/api';

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function describeSendModeSwitchLosses(
  envelope: Pick<
    DocumentSignEnvelopeDetailResponse,
    'sendMode' | 'signingOrder' | 'recipients' | 'fields' | 'bulkRecipients'
  >,
  next: DocumentSignSendMode,
): string[] {
  const current = envelope.sendMode ?? 'single';
  if (current === next) return [];
  const losses: string[] = [];

  if (next === 'bulk') {
    const nonSigners = envelope.recipients.filter((r) => r.role !== 'signer');
    if (nonSigners.length > 0) {
      losses.push(
        `${plural(nonSigners.length, 'viewer or approver', 'viewers and approvers')} will be removed — each copy has a single signer.`,
      );
    }

    const signerIds = new Set(
      envelope.recipients.filter((r) => r.role === 'signer').map((r) => r.recipientId),
    );
    const signersWithFields = new Set(
      envelope.fields.map((f) => f.recipientId).filter((id) => signerIds.has(id)),
    );
    if (signersWithFields.size > 1) {
      losses.push(
        `Fields placed for ${signersWithFields.size} different signers will become one set that every person fills in — including any meant for another party.`,
      );
    }
    const nonSignerIds = new Set(nonSigners.map((r) => r.recipientId));
    const nonSignerFields = envelope.fields.filter((f) => nonSignerIds.has(f.recipientId));
    if (nonSignerFields.length > 0) {
      losses.push(
        `${plural(nonSignerFields.length, 'field', 'fields')} placed for a viewer or approver will move to each person's copy.`,
      );
    }

    if (envelope.recipients.some((r) => r.role === 'signer' && r.requiresAccessCode)) {
      losses.push('Access codes already set will be cleared.');
    }
    if (envelope.signingOrder === 'sequential') {
      losses.push('The signing order will no longer apply.');
    }
    return losses;
  }

  // bulk → single
  const people = envelope.bulkRecipients ?? [];
  if (people.length === 0) {
    if (envelope.fields.length > 0) {
      losses.push(
        `There is no one on the list to give them to, so ${plural(envelope.fields.length, 'placed field', 'placed fields')} will be removed.`,
      );
    }
    return losses;
  }
  if (people.length > 1 && envelope.fields.length > 0) {
    const first = people[0].name || people[0].email;
    losses.push(
      `Every placed field will belong to ${first} only. The other ${plural(people.length - 1, 'person', 'people')} will need fields of their own before you can send.`,
    );
  }
  return losses;
}
